import { useEffect, useMemo, useState } from 'react'
import { BarChart3, CalendarDays, CreditCard, Download, Loader2, RefreshCw, Users, Wallet } from 'lucide-react'
import api from '../../utils/api'
import { exportRowsToExcel } from '../../utils/exportExcel'
import PackageSearchSelect from './PackageSearchSelect'

function formatDateText(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function addDays(dateText, days) {
  const [year, month, day] = String(dateText).split('-').map((item) => parseInt(item, 10))
  const date = new Date(year, month - 1, day)
  date.setDate(date.getDate() + days)
  return formatDateText(date)
}

function getDefaultStartDate(days) {
  return addDays(formatDateText(new Date()), -(days - 1))
}

function formatAmount(value) {
  const amount = Number(value || 0)
  return Number.isFinite(amount) ? amount.toFixed(2) : '0.00'
}

function formatPercent(value) {
  const percent = Number(value || 0)
  return `${Number.isFinite(percent) ? percent.toFixed(2) : '0.00'}%`
}

export default function ChannelConversionStatsTab() {
  const getDefaultEndDate = () => formatDateText(new Date())
  const [loading, setLoading] = useState(true)
  const [startDate, setStartDate] = useState(getDefaultStartDate(7))
  const [endDate, setEndDate] = useState(getDefaultEndDate())
  const [sourceLabels, setSourceLabels] = useState([])
  const [filters, setFilters] = useState({ startDate: getDefaultStartDate(7), endDate: getDefaultEndDate(), sourceLabels: [] })
  const [summary, setSummary] = useState({
    total_new_users: 0,
    total_used_users: 0,
    total_unused_users: 0,
    total_recharge_users: 0,
    total_second_paid_users: 0,
    total_three_plus_paid_users: 0,
    total_recharge_order_count: 0,
    total_recharge_amount: 0,
    usage_rate: 0,
    recharge_rate: 0,
    second_paid_rate: 0,
    three_plus_paid_rate: 0,
    source_count: 0
  })
  const [list, setList] = useState([])
  const [range, setRange] = useState({ start_date: getDefaultStartDate(7), end_date: getDefaultEndDate() })
  const [sourceOptions, setSourceOptions] = useState([])

  const fetchData = async (nextFilters = filters) => {
    setLoading(true)
    try {
      const response = await api.get('/admin/channel-conversion-stats', {
        params: {
          start_date: nextFilters.startDate,
          end_date: nextFilters.endDate,
          days: 7,
          source_labels: Array.isArray(nextFilters.sourceLabels) && nextFilters.sourceLabels.length > 0
            ? nextFilters.sourceLabels
            : undefined
        }
      })

      if (response.data.success) {
        const data = response.data.data || response.data || {}
        setSummary(data.summary || {})
        setList(data.list || [])
        setSourceOptions(data.source_options || [])
        setRange({
          start_date: data.start_date || nextFilters.startDate,
          end_date: data.end_date || nextFilters.endDate
        })
      }
    } catch (error) {
      console.error('获取渠道转化统计失败:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData(filters)
  }, [])

  const handleSubmit = (e) => {
    e.preventDefault()
    const nextFilters = { startDate, endDate, sourceLabels }
    setFilters(nextFilters)
    fetchData(nextFilters)
  }

  const handleQuickRange = (nextDays) => {
    const nextStartDate = getDefaultStartDate(nextDays)
    const nextEndDate = getDefaultEndDate()
    setStartDate(nextStartDate)
    setEndDate(nextEndDate)
    const nextFilters = { startDate: nextStartDate, endDate: nextEndDate, sourceLabels }
    setFilters(nextFilters)
    fetchData(nextFilters)
  }

  const handleReset = () => {
    const nextDays = 7
    const nextStartDate = getDefaultStartDate(nextDays)
    const nextEndDate = getDefaultEndDate()
    setStartDate(nextStartDate)
    setEndDate(nextEndDate)
    setSourceLabels([])
    const nextFilters = { startDate: nextStartDate, endDate: nextEndDate, sourceLabels: [] }
    setFilters(nextFilters)
    fetchData(nextFilters)
  }

  const displayRows = useMemo(() => {
    return [...(list || [])].sort((a, b) => {
      const newCompare = Number(b.new_user_count || 0) - Number(a.new_user_count || 0)
      if (newCompare !== 0) return newCompare

      const rechargeCompare = Number(b.recharge_user_count || 0) - Number(a.recharge_user_count || 0)
      if (rechargeCompare !== 0) return rechargeCompare

      return String(a.source_label || '').localeCompare(String(b.source_label || ''))
    })
  }, [list])

  const handleExport = () => {
    if (displayRows.length === 0) return
    const rows = displayRows.slice(0, 1000)
    const truncated = displayRows.length > 1000

    exportRowsToExcel({
      fileName: `渠道转化统计_${range.start_date}_${range.end_date}${truncated ? '_最新1000行' : ''}`,
      sheetName: '渠道转化统计',
      columns: [
        { header: '渠道', key: 'source_label', width: 16 },
        { header: '新增用户数', value: (row) => Number(row.new_user_count || 0), width: 12 },
        { header: '已使用用户数', value: (row) => Number(row.used_user_count || 0), width: 14 },
        { header: '充值金额', value: (row) => Number(formatAmount(row.recharge_amount)), width: 12, format: '0.00' },
        { header: '使用率(%)', value: (row) => Number(formatAmount(row.usage_rate)), width: 10, format: '0.00' },
        { header: '免费用户使用率(%)', value: (row) => Number(formatAmount(row.free_usage_rate)), width: 16, format: '0.00' },
        { header: '充值人数', value: (row) => Number(row.recharge_user_count || 0), width: 10 },
        { header: '充值率(%)', value: (row) => Number(formatAmount(row.recharge_rate)), width: 10, format: '0.00' },
        { header: '当日注册充值人数', value: (row) => Number(row.same_day_recharge_count || 0), width: 16 },
        { header: '当日注册充值率(%)', value: (row) => Number(formatAmount(row.same_day_recharge_rate)), width: 16, format: '0.00' },
        { header: '非当日首充人数', value: (row) => Number(row.cross_day_first_recharge_count || 0), width: 14 },
        { header: '非当日首充率(%)', value: (row) => Number(formatAmount(row.cross_day_first_recharge_rate)), width: 14, format: '0.00' },
        { header: '二次付费人数', value: (row) => Number(row.second_paid_user_count || 0), width: 12 },
        { header: '二次付费占比(%)', value: (row) => Number(formatAmount(row.second_paid_rate)), width: 14, format: '0.00' },
        { header: '3次以上付费人数', value: (row) => Number(row.three_plus_paid_user_count || 0), width: 14 },
        { header: '3次以上付费占比(%)', value: (row) => Number(formatAmount(row.three_plus_paid_rate)), width: 16, format: '0.00' },
        { header: '充值订单数', value: (row) => Number(row.recharge_order_count || 0), width: 12 }
      ],
      rows
    })

    if (truncated) {
      alert('数据超过 1000 行，已导出最新 1000 行')
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">渠道转化统计</h2>
          <p className="mt-1 text-sm text-gray-500">按新增用户所属渠道统计使用率、充值率、二次付费和 3 次以上付费占比</p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <CalendarDays className="h-4 w-4 text-gray-400" />
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border-0 bg-transparent text-sm text-gray-700 outline-none"
            />
          </div>

          <span className="text-sm text-gray-400">～</span>

          <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <CalendarDays className="h-4 w-4 text-gray-400" />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border-0 bg-transparent text-sm text-gray-700 outline-none"
            />
          </div>

          <PackageSearchSelect
            value={sourceLabels}
            onChange={setSourceLabels}
            options={sourceOptions}
            multiple
            placeholder="全部渠道"
            searchPlaceholder="搜索渠道名"
            className="min-w-[220px]"
          />

          <button type="submit" className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700">
            查询
          </button>
          <button
            type="button"
            onClick={() => handleQuickRange(7)}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            最近7天
          </button>
          <button
            type="button"
            onClick={() => handleQuickRange(30)}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            最近30天
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            重置
          </button>
          <button
            type="button"
            onClick={() => fetchData(filters)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" />
            刷新
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={loading || list.length === 0}
            className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
          >
            <Download className="h-4 w-4" />
            导出 Excel
          </button>
        </form>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white px-5 py-4 text-sm text-gray-600 shadow-sm">
        当前区间：{range.start_date} 至 {range.end_date}
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">新增用户总数</div>
          <div className="mt-2 flex items-center gap-2 text-2xl font-bold text-blue-600">
            <Users className="h-5 w-5" />
            {loading ? <Loader2 className="h-6 w-6 animate-spin text-gray-400" /> : summary.total_new_users || 0}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">已使用用户 / 使用率</div>
          <div className="mt-2 flex items-center gap-2 text-2xl font-bold text-emerald-600">
            <BarChart3 className="h-5 w-5" />
            {loading ? <Loader2 className="h-6 w-6 animate-spin text-gray-400" /> : `${summary.total_used_users || 0} / ${formatPercent(summary.usage_rate)}`}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">充值人数 / 充值率</div>
          <div className="mt-2 flex items-center gap-2 text-2xl font-bold text-orange-600">
            <Wallet className="h-5 w-5" />
            {loading ? <Loader2 className="h-6 w-6 animate-spin text-gray-400" /> : `${summary.total_recharge_users || 0} / ${formatPercent(summary.recharge_rate)}`}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">二次付费人数 / 占比</div>
          <div className="mt-2 flex items-center gap-2 text-2xl font-bold text-purple-600">
            <CreditCard className="h-5 w-5" />
            {loading ? <Loader2 className="h-6 w-6 animate-spin text-gray-400" /> : `${summary.total_second_paid_users || 0} / ${formatPercent(summary.second_paid_rate)}`}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">充值总额 / 渠道数</div>
          <div className="mt-2 text-2xl font-bold text-rose-600">
            {loading ? <Loader2 className="h-6 w-6 animate-spin text-gray-400" /> : `${formatAmount(summary.total_recharge_amount)} / ${summary.source_count || 0}`}
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">渠道</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">新增用户数</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">已使用用户数</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">充值金额</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">使用率</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">免费用户使用率</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">充值人数/充值率</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">当日注册充值人数/充值率</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">非当日首充人数/充值率</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">二次付费人数</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">二次付费占比</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">3次以上付费人数</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">3次以上付费占比</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">充值订单数</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <tr>
                  <td colSpan={14} className="px-6 py-10 text-center text-sm text-gray-500">
                    <Loader2 className="mx-auto h-6 w-6 animate-spin text-blue-600" />
                  </td>
                </tr>
              ) : displayRows.length > 0 ? (
                displayRows.map((item) => (
                  <tr key={item.source_label} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{item.source_label}</td>
                    <td className="px-6 py-4 text-sm font-medium text-blue-600">{item.new_user_count || 0}</td>
                    <td className="px-6 py-4 text-sm font-medium text-emerald-600">{item.used_user_count || 0}</td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{formatAmount(item.recharge_amount)}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{formatPercent(item.usage_rate)}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{formatPercent(item.free_usage_rate)}</td>
                    <td className="px-6 py-4 text-sm font-medium text-orange-600">{item.recharge_user_count || 0} / {formatPercent(item.recharge_rate)}</td>
                    <td className="px-6 py-4 text-sm font-medium text-orange-600">{item.same_day_recharge_count || 0} / {formatPercent(item.same_day_recharge_rate)}</td>
                    <td className="px-6 py-4 text-sm font-medium text-orange-600">{item.cross_day_first_recharge_count || 0} / {formatPercent(item.cross_day_first_recharge_rate)}</td>
                    <td className="px-6 py-4 text-sm font-medium text-purple-600">{item.second_paid_user_count || 0}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{formatPercent(item.second_paid_rate)}</td>
                    <td className="px-6 py-4 text-sm font-medium text-rose-600">{item.three_plus_paid_user_count || 0}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{formatPercent(item.three_plus_paid_rate)}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{item.recharge_order_count || 0}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={14} className="px-6 py-10 text-center text-sm text-gray-500">
                    暂无数据
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
